import { useCallback, useEffect, useRef } from 'react';
import { sendPatchesFromDrivePreset } from '@aelive/dds-client';
import { getEventName, getTeamFirstName } from '../utils/playerName';
import { buildApplyDataPayload } from '../utils/buildApplyDataPayload';
import { getGraphicForCaption } from '../config/captionGraphicMap';
import { logCommand, updateCommandLog } from '../utils/commandLog';
//TODO: need library to replace all this, and make it generic

/**
 * OPUS-1271 — animate-out helpers for the "Clear Output" control.
 *
 * Renderer overlay actions follow a <ROOT>_IN / <ROOT>_OUT / <ROOT>_PREVIEW
 * convention (see utils/buttonActionMap.js): CARD_IN→CARD_OUT, SUBSTITUTION_IN→
 * SUBSTITUTION_OUT, MATCH_STATS_IN→MATCH_STATS_OUT, etc.
 *
 * IMPORTANT — the Maestro form-lib's resolveActionMode dispatches EVERY overlay
 * take as a BAKE then an ANIMATION on the same <ROOT>_IN (bare "IN" too), and
 * every OUT as an ANIMATION. So we must NOT filter on the presence of a mode
 * (that would exclude every real take); we filter on the action root instead.
 *
 * CLEARABLE_OVERLAY_ROOTS is the allow-list of roots that own a real scoped
 * <ROOT>_OUT. Deliberately EXCLUDED:
 *   - the bare generic "IN"/"OUT": these drive the PERSISTENT clock+score graphic
 *     (gfx_PermScore), whose "OUT" is a layer-level stop that would take the whole
 *     scoreboard off air — never a "clear the last overlay" target;
 *   - TEAM_NAMES_IN: no TEAM_NAMES_OUT is defined in the graphic set or keymap.
 * Score/clock pushes (TEAM_x_SCORE_UPDATE) and silent state pushes (APPLY_DATA)
 * are excluded structurally (not <ROOT>_IN) and by the PREVIEW/APPLY_DATA guard.
 */
const CLEARABLE_OVERLAY_ROOTS = new Set([
  "CARD", "MATCH_STATS", "SUBSTITUTION", "GOALSCORER", "ADDITIONAL_TIME",
  "ATTACKING_THREAT", "FREE_TEXT", "SPONSOR", "WIN_PROBABILITY", "RED_CARDS",
  "TOUCH_MAP", "ATTACKING_THIRDS", "PENALTY_MAP", "GOLDEN_BOOT", "GOALKEEPER_MAP", "APP_PROMO",
]);

// "MATCH_STATS_IN" -> "MATCH_STATS" when it is a known clearable overlay, else null.
const overlayInRoot = (action) => {
  const m = String(action || "").match(/^(.+)_IN$/);
  return m && CLEARABLE_OVERLAY_ROOTS.has(m[1]) ? m[1] : null;
};

export const deriveOutAction = (action) => {
  const root = overlayInRoot(action);
  return root ? `${root}_OUT` : null;
};

// A clearable animate-IN: a <ROOT>_IN for a known overlay root. Tracked whether it
// rides as BAKE or ANIMATION (that is how Maestro sends a take — both carry the
// same {layer, action}). Only the off-air PREVIEW pass and the silent APPLY_DATA
// state push are skipped.
export const isTrackableInAction = (action, mode) => {
  if (mode === "PREVIEW" || mode === "APPLY_DATA") return false;
  return deriveOutAction(action) !== null;
};

export const isOutAction = (action) => {
  const a = String(action || "");
  return a === "OUT" || /_OUT$/.test(a);
};

// The persistent clock+score graphic; Clear Output never takes it off air.
const BASE_LAYER = getGraphicForCaption("Main");

// OUTs for everything on air except the base PermScore, which only loses its sub-graphics.
// `last` covers an overlay a later score update on the base layer hides from `actionState`.
export const outCommandsFor = (actionState, last, renderer) => {
  const outs = [];
  const add = (layer, action, onRenderer = renderer) => {
    if (action && !outs.some((o) => o.layer === layer && o.action === action)) {
      outs.push({ renderer: onRenderer, layer, action });
    }
  };
  Object.entries(actionState || {}).forEach(([layer, action]) => {
    if (!action || isOutAction(action)) return;
    add(layer, layer === BASE_LAYER ? deriveOutAction(action) : "OUT");
  });
  if (last) add(last.layer, deriveOutAction(last.action), last.renderer);
  return outs;
};

export const useBrokerService = (rendererId, selectedRenderer, { isRendererLocked = false, lockedByName = null, onLockedCommandSuppressed = null } = {}) => {

    const getRendererIdToUse = () => {
        return selectedRenderer?.renderer_id || rendererId || "OP-BLUEBIRD-TEST929";
    };

    // Pending +4s Score-finalise timeouts per team side. Stored in a ref so
    // a subsequent score update for the same team clears the older pending
    // BAKE (otherwise rapid score changes queue stale BAKEs that fire after
    // the latest value is already on-air). Cleared on unmount too — without
    // this, navigating away mid-animation would emit a BAKE against a
    // renderer the operator no longer owns.
    const pendingScoreFinaliseRef = useRef({ home: null, away: null });

    // OPUS-1271 — the last clearable overlay the broker actually accepted for an
    // animate-in, so the operator can "Clear Output" (animate out the last GFX)
    // from anywhere, regardless of the current menu / filter / active-graphic
    // selection. Holds only command metadata ({ renderer, layer, action }) — never
    // lineup or player data — and is nulled when that overlay animates out.
    // Recorded in sendMutation's success path (below).
    const lastAnimatedRef = useRef(null);

    useEffect(() => {
        const pending = pendingScoreFinaliseRef.current;
        return () => {
            if (pending.home) clearTimeout(pending.home);
            if (pending.away) clearTimeout(pending.away);
            pending.home = null;
            pending.away = null;
        };
    }, []);

    const sendMutation = async (payload) => {
        // Broker choke-point: EVERY graphic command in AE Drive funnels through
        // here (updateRenderer / card / goal / score / APPLY_DATA / clearOutput,
        // plus the Maestro send path and clock actions). When the renderer is
        // locked for this (non-exempt) user, suppress the command entirely — no
        // network call reaches the broker, so no graphics change. Returns a benign
        // shape so awaiting callers don't throw. This is the SOLE enforcement point
        // for the lock: all data/event actions (goals, cards, event edits/deletes,
        // match reset, etc.) proceed normally and unguarded elsewhere — only
        // graphics reaching the renderer are blocked, here.
        const logEntry = logCommand({ source: 'broker', payload });
        if (isRendererLocked) {
            console.warn('[brokerService] renderer locked — broker command suppressed', {
                layer: payload?.layer, action: payload?.caption?.action,
            });
            onLockedCommandSuppressed?.(lockedByName);
            updateCommandLog(logEntry.id, { status: 'suppressed' });
            return { data: { updateRenderer: null }, suppressed: true };
        }
        try {
            const response = await fetch(process.env.REACT_APP_BROKER_HTTP_URL || "https://aero-broker-dev.aelive.cloud/graphql", {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                    operationName: "MyMutation",
                    variables: { payload },
                    query: `mutation MyMutation($payload: CommandInput!) {
            updateRenderer(payload: $payload) {
              renderer
              layer
              __typename
            }
          }`,
                }),
            });

            if (!response.ok) {
                const errorText = await response.text();
                console.error('[brokerService] Response error:', response.status, errorText);
                throw new Error(`HTTP ${response.status}: ${errorText}`);
            }

            const result = await response.json();
            if (result.errors) {
                throw new Error(result.errors.map(e => e.message).join(', '));
            }

            // OPUS-1271 — remember the last graphic the broker accepted so it can
            // be animated out later. Only real animate-in overlays are tracked; an
            // OUT for the tracked layer just flips its `out` flag. Wrapped so a
            // tracking slip can never break an otherwise-successful dispatch.
            try {
                const cap = payload?.caption || {};
                if (isTrackableInAction(cap.action, cap.mode)) {
                    lastAnimatedRef.current = {
                        renderer: payload.renderer,
                        layer: payload.layer,
                        action: cap.action,
                    };
                } else if (isOutAction(cap.action)) {
                    // The tracked overlay animated out — via its own auto-out, an
                    // operator OUT in Maestro (rides as mode ANIMATION), or our own
                    // clearOutput echo. Forget it so a later Clear Output is an
                    // accurate no-op. Match on the tracked overlay's OWN root: in
                    // production every overlay shares the gfx_PermScore layer, so a
                    // layer match alone would let a DIFFERENT overlay's OUT (e.g. a
                    // card's 5s auto-out) wrongly forget a still-on-air graphic. A
                    // bare layer-level "OUT" takes the whole layer down, so it
                    // always forgets.
                    const last = lastAnimatedRef.current;
                    if (
                        last &&
                        last.layer === payload.layer &&
                        (cap.action === "OUT" || deriveOutAction(last.action) === cap.action)
                    ) {
                        lastAnimatedRef.current = null;
                    }
                }
            } catch (_) { /* tracking is best-effort, never fatal */ }

            updateCommandLog(logEntry.id, { status: 'success' });
            return result;
        } catch (error) {
            console.error("[brokerService] Broker send error:", error);
            updateCommandLog(logEntry.id, { status: 'error', error: error?.message });
            throw error;
        }
    };

    const updateRenderer = async ({ renderer, layer, action, mode, data }) => {
        const payload = {
            renderer: renderer || getRendererIdToUse(),
            layer: layer,
            caption: {
                id: layer,
                action: action,
                ...(mode ? { mode } : {}),
                data: data,
            }
        };
        return sendMutation(payload);
    };

    const sendCardGraphicUpdate = async (teamSide, player, cardType) => {
        const rendererIdToUse = getRendererIdToUse();
        const layerId = getGraphicForCaption("Cards");

        // Event-context name: unique_name → short last → player_last_name
        const secondName = getEventName(player);
        const shirtNumber = String(player.shirt_number || "");

        const iWhichTeam = teamSide === "home" ? 1 : 2;
        const iCardType = cardType === 'red' ? 3 : cardType === 'yellow' ? 1 : 2;

        const cardData = {
            [layerId]: {
                Cards: {
                    iCardType: iCardType,
                    iWhichTeam: iWhichTeam,
                    strSecondName: secondName,
                    strShirtNumber: shirtNumber,
                    strTitle: cardType === 'red' ? "Red Card" : cardType === 'yellow' ? "Yellow Card" : "2nd Yellow Card"
                }
            }
        };

        try {
            // 1. Send CARD_PREVIEW with data
            await updateRenderer({
                renderer: rendererIdToUse,
                layer: layerId,
                action: 'CARD_PREVIEW',
                data: cardData
            });

            // 2. Wait 500ms then send CARD_IN without data
            setTimeout(async () => {
                await updateRenderer({
                    renderer: rendererIdToUse,
                    layer: layerId,
                    action: 'CARD_IN',
                    data: {}
                });

                // 3. Wait 5s then send CARD_OUT without data
                setTimeout(async () => {
                    await updateRenderer({
                        renderer: rendererIdToUse,
                        layer: layerId,
                        action: 'CARD_OUT',
                        data: {}
                    });
                }, 5000);
            }, 500);

        } catch (e) {
            console.error("Failed to send card update", e);
        }
    };

    const sendGoalGraphicUpdate = async (teamSide, updatedScore, player, homeTeam, awayTeam) => {
        const rendererIdToUse = getRendererIdToUse();
        const layerId = getGraphicForCaption("GoalScorer");

        const homeTricode = homeTeam?.team_tricode || "HOME";
        const awayTricode = awayTeam?.team_tricode || "AWY";

        const homeScore = Number(updatedScore.home_team_score ?? 0);
        const awayScore = Number(updatedScore.away_team_score ?? 0);

        // Goal scorer is event-context — short-first for the banner first name,
        // unique_name / short-last / player_last_name for the surname.
        const firstName = getTeamFirstName(player);
        const secondName = getEventName(player);
        const shirtNumber = player.shirt_number || "";

        const action = teamSide === "home" ? "TEAM_1_SCORE_UPDATE" : "TEAM_2_SCORE_UPDATE";

        const goalData = {
            [layerId]: {
                Main: {
                    iClockNumber: 0,
                    iClockRedTime: 99999,
                    bMobileOptimisedActive: true,
                    Team1: {
                        strTeamTricode: homeTricode,
                        strTeamName: homeTeam?.team_name || "",
                        strSetStyle: "",
                        iRedCardTabs: 0,
                        // Only send Score_UD for the scoring team on the 1st BAKE
                        ...(teamSide === "home"
                            ? { Score_UD: { strScore: String(homeScore) } }
                            : { Score: { strScore: String(homeScore) }, Score_UD: { strScore: String(homeScore) } }
                        ),
                    },
                    Team2: {
                        strTeamTricode: awayTricode,
                        strTeamName: awayTeam?.team_name || "",
                        strSetStyle: "",
                        iRedCardTabs: 0,
                        // Only send Score_UD for the scoring team on the 1st BAKE
                        ...(teamSide === "away"
                            ? { Score_UD: { strScore: String(awayScore) } }
                            : { Score: { strScore: String(awayScore) }, Score_UD: { strScore: String(awayScore) } }
                        ),
                    },
                },
                AdditionalTime: { strTitle: "", strValue: "" },
                AttackingThreat: {
                    Team1: { iBarScale: "" },
                    Team2: { iBarScale: "" },
                    strSubtitle: "",
                    strTitle: "",
                },
                Cards: {
                    iCardType: "",
                    iWhichTeam: "",
                    strSecondName: "",
                    strShirtNumber: "",
                    strTitle: "",
                },
                FreeText: {
                    Lines: {
                        Line1: { strTitle: "" },
                        Line2: { strTitle: "" },
                    },
                },
                GoalScorer: {
                    strFirstName: firstName,
                    strSecondName: secondName,
                    strShirtNumber: String(shirtNumber),
                    strPlayerVideo: "",
                    strTeamFlag: "",
                    strTitle: "Goal",
                },
                Sponsor: { iWhichType: 2 },
            },
        };

        try {
            // 1. Send BAKE immediately with Score_UD only for the scoring team
            await updateRenderer({
                renderer: rendererIdToUse,
                layer: layerId,
                action: action,
                mode: 'BAKE',
                data: goalData
            });

            // 2. Immediately send ANIMATION
            await updateRenderer({
                renderer: rendererIdToUse,
                layer: layerId,
                action: action,
                mode: 'ANIMATION',
                data: {}
            });

        } catch (e) {
            console.error("Failed to set goal graphic", e);
        }
    };

    const triggerScoreUpdate = async (teamSide, updatedScore, homeTeam, awayTeam, skipAnimation = false) => {
        const rendererIdToUse = getRendererIdToUse();
        const layerId = getGraphicForCaption("Main");

        const isHome = teamSide === 'home';
        const updateAction = isHome ? "TEAM_1_SCORE_UPDATE" : "TEAM_2_SCORE_UPDATE";
        const scoreVal = String(isHome ? updatedScore.home_team_score : updatedScore.away_team_score);

        const dataUD = {
            [layerId]: {
                Main: {
                    [isHome ? "Team1" : "Team2"]: {
                        Score_UD: { strScore: scoreVal }
                    }
                }
            }
        };

        const dataScore = {
            [layerId]: {
                Main: {
                    [isHome ? "Team1" : "Team2"]: {
                        Score: { strScore: scoreVal }
                    }
                }
            }
        };

        const commands = [
            { subscriptionToCommand: { renderer: rendererIdToUse, layer: layerId, GPI: null, DataConnector: null, caption: { id: layerId, action: 'PREVIEW', option: null, data: dataUD } } },
            { subscriptionToCommand: { renderer: rendererIdToUse, layer: layerId, GPI: null, DataConnector: null, caption: { id: layerId, action: updateAction, option: null, data: {} } } },
            { subscriptionToCommand: { renderer: rendererIdToUse, layer: layerId, GPI: null, DataConnector: null, caption: { id: layerId, action: null, option: null, data: dataScore } } },
        ];

        try {
            if (skipAnimation) {
                // Just send a direct BAKE with the final Score to avoid animation
                await updateRenderer({
                    renderer: rendererIdToUse,
                    layer: layerId,
                    action: updateAction,
                    mode: 'BAKE',
                    data: dataScore
                });
            } else {
                // 1. Send BAKE immediately with Score_UD
                await updateRenderer({
                    renderer: rendererIdToUse,
                    layer: layerId,
                    action: updateAction,
                    mode: 'BAKE',
                    data: dataUD
                });

                // 2. Immediately send ANIMATION
                await updateRenderer({
                    renderer: rendererIdToUse,
                    layer: layerId,
                    action: updateAction,
                    mode: 'ANIMATION',
                    data: {}
                });

                // 3. After 4s, send Score BAKE to finalise the renderer state.
                // Cancel any prior pending finalise for this team — back-to-back
                // score updates should only commit the latest value, not queue
                // stale BAKEs that fire after the new score is already shown.
                const sideKey = isHome ? 'home' : 'away';
                if (pendingScoreFinaliseRef.current[sideKey]) {
                    clearTimeout(pendingScoreFinaliseRef.current[sideKey]);
                }
                pendingScoreFinaliseRef.current[sideKey] = setTimeout(async () => {
                    pendingScoreFinaliseRef.current[sideKey] = null;
                    try {
                        await updateRenderer({
                            renderer: rendererIdToUse,
                            layer: layerId,
                            action: updateAction,
                            mode: 'BAKE',
                            data: dataScore
                        });
                    } catch (e) {
                        console.error("Failed to send delayed Score BAKE", e);
                    }
                }, 4000);
            }
        } catch (e) {
            console.error("Failed to trigger score update", e);
        }

        return commands;
    };

    /**
     * Push the current authoritative state to the renderer without playing an
     * animation. Used after event removals / unwinds / resets where we want
     * the broker's view of the score, red-card tabs and any card animation
     * state to match the new truth without re-running an animation.
     *
     * Payload mirrors the example shape:
     *   { action: "IN", mode: "APPLY_DATA", data: { gfx_PermScore: { Main: {...} } } }
     *
     * Score is sent as `Score.strScore` AND `Score.strScore_UD` set to the
     * same value for each team so any subsequent animation that diffs from
     * `Score_UD` lands at the right starting point.
     *
     * @param {Object} args
     * @param {{home: number|string, away: number|string}} args.scores
     * @param {Object} args.homeTeam
     * @param {Object} args.awayTeam
     * @param {{home: number, away: number}} [args.redCardTabs] If omitted the
     *        field is sent as "" (template default). Pass the running per-team
     *        red-card counts (red_card + double_yellow) so the clock graphic's
     *        red tab counter reflects the corrected state.
     * @param {boolean} [args.clearCards=false] Send a Cards block of blanks.
     *        Off by default: every caption shares the gfx_PermScore layer, so a
     *        Cards block of empty strings blanks iWhichTeam and takes the whole
     *        graphic into a blank state on air — goal cancel was doing exactly
     *        that (OPUS-1302). The event-service delete builders dropped it for
     *        the same reason.
     * @param {string} [args.renderer]
     */
    const sendApplyDataState = async ({
        scores,
        homeTeam,
        awayTeam,
        redCardTabs,
        clearCards = false,
        renderer,
    } = {}) => {
        const rendererIdToUse = renderer || getRendererIdToUse();
        const layerId = getGraphicForCaption("Main");

        const data = buildApplyDataPayload({
            scores,
            homeTeam,
            awayTeam,
            redCardTabs,
            clearCards,
            graphic: layerId,
        });

        try {
            await updateRenderer({
                renderer: rendererIdToUse,
                layer: layerId,
                action: "IN",
                mode: "APPLY_DATA",
                data,
            });
        } catch (e) {
            console.error("Failed to send APPLY_DATA state", e);
        }
    };

    // "Clear Output": animate out everything on screen except the base PermScore,
    // with empty data so no lineup or player data is touched.
    const clearOutput = async (actionState) => {
        const outs = outCommandsFor(actionState, lastAnimatedRef.current, getRendererIdToUse());
        if (!outs.length) return { cleared: false, reason: "nothing-active" };
        try {
            for (const { renderer, layer, action } of outs) {
                await updateRenderer({ renderer, layer, action, data: {} });
            }
            lastAnimatedRef.current = null;
            return { cleared: true, outs: outs.map(({ layer, action }) => ({ layer, action })) };
        } catch (e) {
            console.error("Failed to clear output", e);
            return { cleared: false, reason: "send-failed" };
        }
    };

    // OPUS-1271 — read-only peek at the last animated-in graphic (UI/tests).
    const getLastAnimated = () => lastAnimatedRef.current;

    const cancelPendingScoreFinalise = (side) => {
        const sides = side ? [side] : ['home', 'away'];
        sides.forEach((s) => {
            if (pendingScoreFinaliseRef.current[s]) {
                clearTimeout(pendingScoreFinaliseRef.current[s]);
                pendingScoreFinaliseRef.current[s] = null;
            }
        });
    };

    return {
        updateRenderer,
        sendCardGraphicUpdate,
        sendGoalGraphicUpdate,
        triggerScoreUpdate,
        sendApplyDataState,
        getRendererIdToUse,
        sendMutation,
        clearOutput,
        getLastAnimated,
        cancelPendingScoreFinalise,
    };
};
